import {
  getTaskAttachments as getTaskAttachmentsRpc,
  uploadTaskAttachment as uploadTaskAttachmentRpc,
  deleteTaskAttachment as deleteTaskAttachmentRpc,
} from './taskAttachmentsApi';

// NOTE: All endpoints and methods here mirror existing usage in the app.
// We do NOT change URLs, HTTP verbs, or payload/response shapes.

// ---------------- Task Meta (Kinds & Statuses) ----------------
// Now uses RPC: /api/:company/rpc/admin/task-meta/*

/** POST /api/:company/admin/task-meta/kind - Now uses RPC */
export async function createTaskKind(company: string, payload: { code: string; nameHe: string; order: number }) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.createKind(payload);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create task kind');
}

/** POST /api/:company/admin/task-meta/status - Now uses RPC */
export async function createTaskStatus(company: string, payload: { key: string; nameHe: string; order: number }) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.createStatus(payload);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create task status');
}

/** DELETE /api/:company/admin/task-meta/status/:key - Now uses RPC */
export async function deleteTaskStatus(company: string, key: string) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.deleteStatus(key);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete task status');
}

/** DELETE /api/:company/admin/task-meta/kind/:code - Now uses RPC */
export async function deleteTaskKind(company: string, code: string) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.deleteKind(code);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete task kind');
}

/** PUT /api/:company/admin/task-meta/status/:key - Now uses RPC */
export async function updateTaskStatus(company: string, key: string, payload: { key: string; nameHe: string; order?: number }) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.updateStatus({ oldKey: key, ...payload });
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update task status');
}

/** PUT /api/:company/admin/task-meta/kind/:code - Now uses RPC */
export async function updateTaskKind(company: string, code: string, payload: { code: string; nameHe: string; order?: number }) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.updateKind({ oldCode: code, ...payload });
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update task kind');
}

/** PATCH /api/:company/admin/task-meta/status/:key/active - Now uses RPC */
export async function toggleTaskStatusActive(company: string, key: string, active: boolean) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.toggleStatusActive(key, active);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to toggle task status active');
}

/** PATCH /api/:company/admin/task-meta/kind/:code/active - Now uses RPC */
export async function toggleTaskKindActive(company: string, code: string, active: boolean) {
  const rpcTaskMeta = (await import('./rpc/rpcTaskMeta')).default;
  const { data } = await rpcTaskMeta.toggleKindActive(code, active);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to toggle task kind active');
}

// ---------------- Admin Tasks ----------------

/** GET /api/:company/admin/tasks - Now uses RPC */
export async function getAdminTasks(company: string, params?: Record<string, any>) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.list(params);
/*   console.log('[getAdminTasks] RPC response:', data);
 */  if (data.ok) {
    // Handle both array and object with tasks property
    const tasksData = Array.isArray(data.data) ? data.data : (data.data?.tasks || data.data || []);
/*     console.log('[getAdminTasks] Returning tasks:', tasksData.length);
 */    return tasksData;
  }
  throw new Error(data.error || 'Failed to get admin tasks');
}

/** GET /api/:company/admin/task/:taskId - Now uses RPC */
export async function getAdminTaskById(company: string, taskId: string) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.getById(taskId);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin task');
}

/** GET /api/:company/admin/task/by-task-id/:taskId - Now uses RPC */
export async function getAdminTaskByTaskId(company: string, taskId: number) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.getByTaskId(taskId);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get admin task by task ID');
}

/** GET /api/:company/admin/tasks/next-id - Now uses RPC */
export async function getAdminTasksNextId(company: string) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.getNextId();
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get next task ID');
}

/** POST /api/:company/admin/task - Now uses RPC */
export async function createAdminTask(company: string, payload: any) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.create(payload);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to create admin task');
}

/** PUT /api/:company/admin/task/:taskId - Now uses RPC */
export async function updateAdminTask(company: string, taskId: string, payload: any) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.update(taskId, payload);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update admin task');
}

/** PUT /api/:company/admin/task/:taskId/transfer - Now uses RPC */
export async function transferAdminTask(company: string, taskId: string, managerId: string) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.transfer(taskId, managerId);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to transfer admin task');
}

/** DELETE /api/:company/admin/task/:taskId - Now uses RPC */
export async function deleteAdminTask(company: string, taskId: string) {
  const rpcAdminTasks = (await import('./rpc/rpcAdminTasks')).default;
  const { data } = await rpcAdminTasks.delete(taskId);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to delete admin task');
}

// ---------------- Manager Tasks ----------------

/** GET /api/:company/manager/task/:taskId - Now uses RPC */
export async function getManagerTaskById(company: string, taskId: string) {
  const rpcManagers = (await import('./rpc/rpcManagers')).default;
  const { data } = await rpcManagers.getTaskById(taskId);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get manager task');
}

/** PUT /api/:company/manager/task/:taskId - Now uses RPC */
export async function updateManagerTask(company: string, taskId: string, payload: any) {
  const rpcManagers = (await import('./rpc/rpcManagers')).default;
  const { data } = await rpcManagers.updateTask(taskId, payload);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to update manager task');
}

/** PUT /api/:company/manager/task/:taskId/complete - Now uses RPC */
export async function completeManagerTask(company: string, taskId: string) {
  const rpcManagers = (await import('./rpc/rpcManagers')).default;
  const { data } = await rpcManagers.completeTask(taskId);
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to complete manager task');
}

/** POST /api/:company/manager/task/:taskId/notes - Now uses RPC */
export async function addManagerTaskNote(company: string, taskId: string, payload: { body: string; visibility: 'manager' | 'all' }) {
  const rpcManagers = (await import('./rpc/rpcManagers')).default;
  const { data } = await rpcManagers.createTaskNote(taskId, payload.body, payload.visibility || 'all');
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to add manager task note');
}

/** POST /api/:company/admin/task/:taskId/notes - Now uses RPC */
export async function addAdminTaskNote(company: string, taskId: string, payload: { body: string; visibility: 'admin' | 'all' | 'manager' }) {
  const rpcManagers = (await import('./rpc/rpcManagers')).default;
  const { data } = await rpcManagers.createTaskNote(taskId, payload.body, payload.visibility || 'admin');
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to add admin task note');
}

// ---------------- Task Attachments ----------------
// Note: Task attachments now use RPC endpoints via taskAttachmentsApi.ts
// These functions are kept for backward compatibility but delegate to the RPC API

/** GET /api/:company/tasks/:taskId/attachments - Now uses RPC */
export async function getTaskAttachments(company: string, taskId: string) {
  const attachments = await getTaskAttachmentsRpc(company, taskId);
  return attachments;
}

/** POST /api/:company/tasks/:taskId/attachments - Now uses RPC */
export async function uploadTaskAttachment(company: string, taskId: string, formData: FormData) {
  const file = formData.get('file') as File;
  const title = formData.get('title') as string | undefined;
  const result = await uploadTaskAttachmentRpc(company, taskId, file, title);
  return result;
}

/** DELETE /api/:company/tasks/:taskId/attachments/:publicId - Now uses RPC */
export async function deleteTaskAttachment(company: string, taskId: string, publicId: string) {
  const result = await deleteTaskAttachmentRpc(company, taskId, publicId);
  return result;
}

// ---------------- Calendar & Slots ----------------

/** GET /api/:company/google/calendar-events */
/** Now uses RPC: POST /api/:company/rpc/meet/google/calendar-events */
export { getGoogleCalendarEvents } from './googleApi';

/** GET /api/:company/admin/slots - Now uses RPC */
export async function getCalendarSlots(company: string, params?: { adminId?: string }) {
  const rpcSlots = (await import('./rpc/rpcSlots')).default;
  const { data } = await rpcSlots.list();
  if (data.ok) {
    return data.data;
  }
  throw new Error(data.error || 'Failed to get calendar slots');
}

/** PUT /api/:company/admin/slot/:slotId - Now uses RPC */
export async function updateSlot(company: string, slotId: string, payload: { maxClients?: number }) {
  const rpcBooking = (await import('./rpc/rpcBooking')).default;
  return rpcBooking.updateSlot(company, slotId, payload);
}

/** DELETE /api/:company/admin/slot/:slotId - Now uses RPC */
export async function deleteSlot(company: string, slotId: string) {
  const rpcBooking = (await import('./rpc/rpcBooking')).default;
  return rpcBooking.deleteSlot(company, slotId);
}

/** POST /api/:company/slots/:slotId/:action (book/cancel) - Now uses RPC */
export async function bookOrCancelSlot(company: string, slotId: string, action: 'book' | 'cancel') {
  const rpcBooking = (await import('./rpc/rpcBooking')).default;
  if (action === 'book') {
    return rpcBooking.bookSlot(company, slotId);
  } else {
    return rpcBooking.cancelSlot(company, slotId);
  }
}

